import React, { useState, useEffect, useCallback } from 'react';
import { Users, Loader2, Search, MessageCircle, ExternalLink, Star, X, Send } from 'lucide-react';
import { toast } from 'sonner';
import { API, API_BASE } from '../../config/apiBase';

const CATEGORIES = ["Todos", "Designer", "Marketing", "Social Media", "Gestor de Tráfego", "Editor", "Copywriter", "Automação", "Suporte técnico"];

function StarRating({ value, onChange, size = 16, readonly = false }) {
    const [hover, setHover] = useState(0);
    return (
        <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map(i => (
                <button key={i} type="button" disabled={readonly}
                    className={`transition-all ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
                    onClick={() => !readonly && onChange?.(i)}
                    onMouseEnter={() => !readonly && setHover(i)}
                    onMouseLeave={() => !readonly && setHover(0)}>
                    <Star size={size} fill={(hover || value) >= i ? '#F5B544' : 'none'} style={{ color: (hover || value) >= i ? '#F5B544' : 'var(--muted)' }} />
                </button>
            ))}
        </div>
    );
}

export default function EmpresaIndicaçõesPage() {
    const [pros, setPros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterCat, setFilterCat] = useState('Todos');
    const [sortBy, setSortBy] = useState('order');
    const [reviewModal, setReviewModal] = useState(null);
    const [reviewRating, setReviewRating] = useState(0);
    const [reviewComment, setReviewComment] = useState('');
    const [reviewsModal, setReviewsModal] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchPros = useCallback(async () => {
        try {
            const res = await fetch(`${API}/indicações`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setPros(await res.json());
        } catch {} finally { setLoading(false); }
    }, [token]);

    useEffect(() => { fetchPros(); }, [fetchPros]);

    const openReview = (p) => { setReviewModal(p); setReviewRating(0); setReviewComment(''); };

    const submitReview = async () => {
        if (!reviewRating) { toast.error('Selecione uma nota'); return; }
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/indicações/${reviewModal.profissional_id}/avaliar`, {
                method: 'POST', headers, body: JSON.stringify({ rating: reviewRating, comment: reviewComment }),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success('Avaliacao enviada!');
                setPros(prev => prev.map(p => p.profissional_id === reviewModal.profissional_id ? { ...p, avg_rating: data.avg_rating, total_reviews: data.total_reviews } : p));
                setReviewModal(null);
            } else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
        } catch { toast.error('Erro de conexão'); } finally { setSubmitting(false); }
    };

    const openReviews = async (p) => {
        setReviewsModal(p);
        try {
            const res = await fetch(`${API}/indicações/${p.profissional_id}/avaliacoes`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setReviews(await res.json());
        } catch {}
    };

    const filtered = (filterCat === 'Todos' ? pros : pros.filter(p => p.category === filterCat))
        .sort((a, b) => sortBy === 'rating' ? ((b.avg_rating || 0) - (a.avg_rating || 0)) : ((a.order || 0) - (b.order || 0)));

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-4" data-testid="empresa-indicações-page">
            {/* Intro */}
            <div className="rounded-xl p-4" style={{ background: 'linear-gradient(135deg, rgba(var(--sv-accent-rgb), 0.08) 0%, rgba(var(--sv-accent-rgb), 0.02) 100%)', border: '1px solid rgba(var(--sv-accent-rgb), 0.15)' }}>
                <div className="flex items-center gap-2 mb-1">
                    <Star size={15} style={{ color: 'var(--sv-gold-primary)' }} />
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Profissionais Recomendados</h3>
                </div>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>Conecte-se com especialistas selecionados. Avalie apos contratar!</p>
            </div>

            {/* Filters */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex gap-1.5 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
                    {CATEGORIES.map(c => (
                        <button key={c} className="px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all"
                            style={{ background: filterCat === c ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface)', border: `1px solid ${filterCat === c ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: filterCat === c ? 'var(--sv-accent)' : 'var(--muted)' }}
                            onClick={() => setFilterCat(c)} data-testid={`cat-filter-${c}`}>{c}</button>
                    ))}
                </div>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                    className="px-2.5 py-1.5 rounded-lg text-[11px]" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="sort-select">
                    <option value="order">Ordem padrão</option>
                    <option value="rating">Melhor avaliados</option>
                </select>
            </div>

            {/* Grid */}
            {filtered.length === 0 ? (
                <div className="text-center py-14 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <Users size={32} className="mx-auto mb-2" style={{ color: 'var(--muted)', opacity: 0.4 }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                        {pros.length === 0 ? 'Nenhum profissional disponível ainda' : 'Nenhum profissional nesta categoria'}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                        {pros.length === 0 ? 'Em breve teremos indicações para você' : 'Tente outra categoria'}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map(p => (
                        <div key={p.profissional_id} className="rounded-xl overflow-hidden transition-all" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`indicacao-${p.profissional_id}`}>
                            {/* Badge destaque */}
                            {(p.avg_rating || 0) >= 4.5 && (p.total_reviews || 0) >= 2 && (
                                <div className="px-3 py-1 text-[10px] font-semibold text-center" style={{ background: 'linear-gradient(90deg, #1756B8, #3D8BFF)', color: '#FFFFFF' }}>
                                    Destaque
                                </div>
                            )}
                            {/* Photo */}
                            <div className="w-full h-32 flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
                                {p.photo_url ? (
                                    <img src={`${API_BASE}${p.photo_url}`} alt={p.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)', color: 'var(--sv-gold-primary)' }}>
                                        {p.name?.charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            {/* Info */}
                            <div className="p-3.5">
                                <div className="flex items-center gap-2 mb-1">
                                    <h4 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{p.name}</h4>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-gold-primary)' }}>{p.category}</span>
                                </div>
                                {p.specialty && <p className="text-xs font-medium mb-1" style={{ color: 'var(--sv-gold-primary)' }}>{p.specialty}</p>}
                                {p.description && <p className="text-[11px] mb-2" style={{ color: 'var(--muted)' }}>{p.description}</p>}

                                {/* Rating */}
                                <div className="flex items-center justify-between mb-3">
                                    <button className="flex items-center gap-1.5" onClick={() => p.total_reviews > 0 ? openReviews(p) : openReview(p)} data-testid={`rating-${p.profissional_id}`}>
                                        <StarRating value={Math.round(p.avg_rating || 0)} readonly size={13} />
                                        <span className="text-[11px] font-medium" style={{ color: 'var(--text)' }}>{p.avg_rating?.toFixed(1) || '0.0'}</span>
                                        <span className="text-[10px]" style={{ color: 'var(--muted)' }}>({p.total_reviews || 0})</span>
                                    </button>
                                    <button className="text-[10px] px-2 py-0.5 rounded-full transition-all hover:opacity-80" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-gold-primary)' }}
                                        onClick={() => openReview(p)} data-testid={`review-btn-${p.profissional_id}`}>Avaliar</button>
                                </div>

                                {/* WhatsApp */}
                                {p.whatsapp ? (
                                    <a href={`https://wa.me/${p.whatsapp}?text=Olá! Vi seu perfil no SuperVitrine e gostaria de saber mais sobre seus servicos.`} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90" style={{ background: '#25D366', color: '#fff' }} data-testid={`whatsapp-${p.profissional_id}`}>
                                        <MessageCircle size={13} /> Falar no WhatsApp
                                    </a>
                                ) : (
                                    <div className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                                        <ExternalLink size={13} /> Contato indisponível
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Footer */}
            <div className="rounded-xl p-3" style={{ background: 'rgba(var(--sv-accent-rgb), 0.04)', border: '1px solid rgba(var(--sv-accent-rgb), 0.1)' }}>
                <p className="text-[11px] text-center" style={{ color: 'var(--muted)' }}>
                    Profissionais selecionados pela equipe SuperVitrine. A contratacao e responsabilidade das partes.
                </p>
            </div>

            {/* Review Modal */}
            {reviewModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) setReviewModal(null); }}>
                    <div className="w-full max-w-sm rounded-2xl p-5" style={{ background: 'var(--surface)', border: '1px solid rgba(var(--sv-accent-rgb), 0.25)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }} data-testid="review-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Avaliar {reviewModal.name}</h3>
                            <button onClick={() => setReviewModal(null)} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}><X size={14} style={{ color: 'var(--muted)' }} /></button>
                        </div>
                        <div className="flex flex-col items-center gap-3 mb-4">
                            <p className="text-xs" style={{ color: 'var(--muted)' }}>Como foi sua experiencia?</p>
                            <StarRating value={reviewRating} onChange={setReviewRating} size={28} />
                            {reviewRating > 0 && <span className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>{['', 'Ruim', 'Regular', 'Bom', 'Muito bom', 'Excelente'][reviewRating]}</span>}
                        </div>
                        <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} rows={3} placeholder="Deixe um comentario (opcional)..."
                            className="w-full px-3 py-2.5 rounded-lg text-sm resize-none mb-4" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="review-comment" />
                        <div className="flex gap-2">
                            <button className="flex-1 px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }} onClick={() => setReviewModal(null)}>Cancelar</button>
                            <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} onClick={submitReview} disabled={submitting} data-testid="submit-review-btn">
                                {submitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Enviar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reviews List Modal */}
            {reviewsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) setReviewsModal(null); }}>
                    <div className="w-full max-w-md max-h-[80vh] overflow-y-auto rounded-2xl p-5" style={{ background: 'var(--surface)', border: '1px solid rgba(var(--sv-accent-rgb), 0.25)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }} data-testid="reviews-list-modal">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-sm font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Avaliacoes de {reviewsModal.name}</h3>
                                <div className="flex items-center gap-2 mt-1">
                                    <StarRating value={Math.round(reviewsModal.avg_rating || 0)} readonly size={13} />
                                    <span className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>{reviewsModal.avg_rating?.toFixed(1) || '0.0'} ({reviewsModal.total_reviews || 0} avaliacoes)</span>
                                </div>
                            </div>
                            <button onClick={() => setReviewsModal(null)} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}><X size={14} style={{ color: 'var(--muted)' }} /></button>
                        </div>
                        {reviews.length === 0 ? (
                            <p className="text-xs text-center py-6" style={{ color: 'var(--muted)' }}>Nenhuma avaliacao ainda</p>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {reviews.map((r, i) => (
                                    <div key={i} className="p-3 rounded-xl" style={{ background: 'var(--surface-2)' }}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>{r.user_name}</span>
                                            <StarRating value={r.rating} readonly size={11} />
                                        </div>
                                        {r.comment && <p className="text-[11px]" style={{ color: 'var(--muted)' }}>{r.comment}</p>}
                                        <p className="text-[10px] mt-1" style={{ color: 'var(--muted)', opacity: 0.5 }}>{r.created_at ? new Date(r.created_at).toLocaleDateString('pt-BR') : ''}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                        <button className="w-full mt-3 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}
                            onClick={() => { setReviewsModal(null); openReview(reviewsModal); }} data-testid="add-review-from-list">
                            <Star size={12} /> Avaliar este profissional
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
